import { Link } from '@inertiajs/react';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { AlertTriangle, User, Users } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { useMutation } from '../../../Shared/api';
import { useLocale } from '../../../Shared/locale';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { Campaign, CampaignType, PaymentMethod, Project } from '../../../Shared/types';
import { ConfirmButton } from '../../../Shared/UI/ConfirmButton';
import { Icon } from '../../../Shared/UI/Icon';
import { useCharityStyle } from '../../../Shared/useCharityStyle';
import { useRouter } from '../../router';
import { Product, SharedProps } from '../../types';
import Container from '../../UI/Container';
import Header from '../../UI/Header';
import HeaderTitle from '../../UI/HeaderTitle';
import Layout from '../../UI/Layout';
import { SettingsLayout } from '../SettingsLayout';
import EditProductForm from './EditProductForm';
import ProductToggle from './ProductToggle';

export interface ProductPageProps extends SharedProps {
  product: Product & {
    projects: Project[];
    campaigns_count: number;
    campaign: Campaign | null;
    deletable: boolean;
  };
  projects: Project[];
  paymentMethods: PaymentMethod[];
}

export default function ProductPage({
  product,
  projects,
  paymentMethods,
  charity,
}: ProductPageProps) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();
  const { formatNumber } = useLocale();

  useCharityStyle(charity);

  const [deleteProduct, { loading: deleting }] = useMutation(routes.delete_product(product.id), {
    onSuccess: () => {
      notifier.notify(t('backend:product_deleted'));
    },
    onError: () => {
      notifier.notify(t('backend:something_went_wrong'), AlertTriangle);
    },
  });

  return (
    <>
      <Header>
        <Container>
          <div className="flex items-center justify-between w-full">
            <HeaderTitle className={classNames(!product.enabled && 'opacity-50')}>
              {product.title}
            </HeaderTitle>

            <ProductToggle product={product} />
          </div>
        </Container>
      </Header>
      <Container>
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-3 space-y-4 text-slate-600" style={{ minWidth: 0 }}>
            {product.campaign && (
              <div>
                <h4 className="font-medium text-slate-500">
                  {t('backend:owner')}
                </h4>
                <Link href={routes.campaign_page(product.campaign.id)}>
                  <Icon className="mr-1">
                    {product.campaign.type === CampaignType.Individual && <User />}
                    {product.campaign.type === CampaignType.Team && <Users />}
                  </Icon>
                  {product.campaign.title}
                </Link>
              </div>
            )}

            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:purchases')}
              </h4>
              {formatNumber(product.sold_count)}
              {product.sold_count > 0 && (
                <span className="text-slate-400">
                  {' · '}
                  <Link href={`${routes.donations_page()}?product_id=${product.id}`}>
                    {t('backend:view')}
                  </Link>
                </span>
              )}
            </div>

            {product.require_opt_in && (
              <div>
                <h4 className="font-medium text-slate-500">
                  {t('backend:campaigns')}
                </h4>
                {formatNumber(product.campaigns_count)}
                {product.campaigns_count > 0 && (
                  <span className="text-slate-400">
                    {' · '}
                    <Link href={`${routes.campaigns_page()}?product_id=${product.id}`}>
                      {t('backend:view')}
                    </Link>
                  </span>
                )}
              </div>
            )}

            {product.deletable && (
              <div>
                <ConfirmButton
                  onClick={() => deleteProduct()}
                  loading={deleting}
                  variant="danger"
                  size="sm"
                >
                  {t('backend:delete_product')}
                </ConfirmButton>
              </div>
            )}
          </div>

          <div className="col-span-9">
            <EditProductForm
              product={product}
              projects={projects}
              paymentMethods={paymentMethods}
            />
          </div>
        </div>
      </Container>
    </>
  );
}

ProductPage.layout = (page: ReactNode) => <Layout><SettingsLayout>{page}</SettingsLayout></Layout>;
